/**
 * FAQ Component - Hochprofessionelle FAQ-Funktionalität
 * 
 * Features:
 * - Live-Suche durch FAQ-Einträge
 * - Smooth Accordion-Animationen
 * - Keyboard Navigation
 * - Accessibility Support
 * - Performance-optimiert
 */

class FAQComponent {
  constructor() {
    this.sections = document.querySelectorAll('.faq-section');
    this.searchInputs = document.querySelectorAll('.faq-search-input');
    this.faqItems = document.querySelectorAll('.faq-item');
    this.faqDetails = document.querySelectorAll('.faq-details');
    
    // Debounce Timer für Suche
    this.searchDebounceTimer = null;
    this.searchDebounceDelay = 300;
    
    // Animation Settings
    this.animationDuration = 300;
    
    this.init();
  }
  
  init() {
    if (this.sections.length === 0) return;
    
    this.setupSearch();
    this.setupAccordions();
    this.setupKeyboardNavigation();
    this.setupAccessibility();
    this.setupAnimations();
    
    console.log('FAQ Component initialized');
  }
  
  /**
   * Setup Search Functionality
   */
  setupSearch() {
    this.searchInputs.forEach(input => {
      input.addEventListener('input', (e) => {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = setTimeout(() => {
          this.performSearch(e.target.value, e.target.closest('.faq-section'));
        }, this.searchDebounceDelay);
      });
      
      // Clear search on Escape
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.target.value = '';
          this.performSearch('', e.target.closest('.faq-section'));
          e.target.blur();
        }
      });
    });
  }
  
  /**
   * Perform FAQ Search
   */
  performSearch(query, section) {
    const faqItems = section.querySelectorAll('.faq-item');
    const resultsCount = section.querySelector('.faq-results-count');
    let visibleCount = 0;
    
    const searchTerm = query.toLowerCase().trim();
    
    faqItems.forEach(item => {
      const question = item.dataset.faqQuestion?.toLowerCase() || '';
      const answer = item.dataset.faqAnswer?.toLowerCase() || '';
      const tags = item.dataset.faqTags?.toLowerCase() || '';
      
      const isMatch = searchTerm === '' || 
                     question.includes(searchTerm) || 
                     answer.includes(searchTerm) || 
                     tags.includes(searchTerm);
      
      if (isMatch) {
        item.classList.remove('faq-item--hidden');
        item.classList.toggle('faq-item--highlighted', searchTerm !== '');
        visibleCount++;
      } else {
        item.classList.add('faq-item--hidden');
        item.classList.remove('faq-item--highlighted');
      }
    });
    
    // Update results count
    if (resultsCount) {
      if (searchTerm === '') {
        resultsCount.textContent = '';
      } else {
        const resultText = visibleCount === 1 ? 'Ergebnis' : 'Ergebnisse';
        resultsCount.textContent = `${visibleCount} ${resultText} gefunden`;
      }
    }
    
    // Auto-open first result if searching
    if (searchTerm !== '' && visibleCount > 0) {
      const firstVisible = section.querySelector('.faq-item:not(.faq-item--hidden) .faq-details');
      if (firstVisible && !firstVisible.open) {
        firstVisible.open = true;
      }
    }
  }
  
  /**
   * Setup Accordion Functionality
   */
  setupAccordions() {
    this.faqDetails.forEach(details => {
      const summary = details.querySelector('.faq-summary');
      
      if (summary) {
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleAccordion(details);
        });
      }
    });
  }
  
  /**
   * Toggle Accordion with Animation
   */
  toggleAccordion(details) {
    const isOpen = details.open;
    const content = details.querySelector('.faq-answer-wrapper');
    
    if (!content) return;
    
    if (isOpen) {
      // Closing
      this.closeAccordion(details, content);
    } else {
      // Opening
      this.openAccordion(details, content);
    }
  }
  
  /**
   * Open Accordion with Animation
   */
  openAccordion(details, content) {
    details.open = true;
    
    // Get the height
    const height = content.scrollHeight;
    
    // Set initial state
    content.style.height = '0px';
    content.style.overflow = 'hidden';
    
    // Force reflow
    content.offsetHeight;
    
    // Animate to full height
    content.style.transition = `height ${this.animationDuration}ms ease`;
    content.style.height = height + 'px';
    
    // Clean up after animation
    setTimeout(() => {
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
    }, this.animationDuration);
  }
  
  /**
   * Close Accordion with Animation
   */
  closeAccordion(details, content) {
    const height = content.scrollHeight;
    
    // Set current height
    content.style.height = height + 'px';
    content.style.overflow = 'hidden';
    
    // Force reflow
    content.offsetHeight;
    
    // Animate to 0
    content.style.transition = `height ${this.animationDuration}ms ease`;
    content.style.height = '0px';
    
    // Close after animation
    setTimeout(() => {
      details.open = false;
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
    }, this.animationDuration);
  }
  
  /**
   * Setup Keyboard Navigation
   */
  setupKeyboardNavigation() {
    this.faqDetails.forEach(details => {
      const summary = details.querySelector('.faq-summary');
      
      if (summary) {
        summary.addEventListener('keydown', (e) => {
          switch (e.key) {
            case 'Enter':
            case ' ':
              e.preventDefault();
              this.toggleAccordion(details);
              break;
            case 'ArrowDown':
              e.preventDefault();
              this.focusNextFAQ(summary);
              break;
            case 'ArrowUp':
              e.preventDefault();
              this.focusPreviousFAQ(summary);
              break;
            case 'Home':
              e.preventDefault();
              this.focusFirstFAQ(summary);
              break;
            case 'End':
              e.preventDefault();
              this.focusLastFAQ(summary);
              break;
          }
        });
      }
    });
  }
  
  /**
   * Focus Navigation Helpers
   */
  focusNextFAQ(currentSummary) {
    const section = currentSummary.closest('.faq-section');
    const summaries = Array.from(section.querySelectorAll('.faq-summary'));
    const currentIndex = summaries.indexOf(currentSummary);
    const nextIndex = (currentIndex + 1) % summaries.length;
    summaries[nextIndex].focus();
  }
  
  focusPreviousFAQ(currentSummary) {
    const section = currentSummary.closest('.faq-section');
    const summaries = Array.from(section.querySelectorAll('.faq-summary'));
    const currentIndex = summaries.indexOf(currentSummary);
    const prevIndex = currentIndex === 0 ? summaries.length - 1 : currentIndex - 1;
    summaries[prevIndex].focus();
  }
  
  focusFirstFAQ(currentSummary) {
    const section = currentSummary.closest('.faq-section');
    const firstSummary = section.querySelector('.faq-summary');
    if (firstSummary) firstSummary.focus();
  }
  
  focusLastFAQ(currentSummary) {
    const section = currentSummary.closest('.faq-section');
    const summaries = section.querySelectorAll('.faq-summary');
    const lastSummary = summaries[summaries.length - 1];
    if (lastSummary) lastSummary.focus();
  }
  
  /**
   * Setup Accessibility
   */
  setupAccessibility() {
    this.faqDetails.forEach((details, index) => {
      const summary = details.querySelector('.faq-summary');
      const content = details.querySelector('.faq-answer-wrapper');
      
      if (summary && content) {
        // Add ARIA attributes
        const summaryId = `faq-summary-${index}`;
        const contentId = `faq-content-${index}`;
        
        summary.id = summaryId;
        summary.setAttribute('aria-expanded', details.open);
        summary.setAttribute('aria-controls', contentId);
        
        content.id = contentId;
        content.setAttribute('aria-labelledby', summaryId);
        
        // Update aria-expanded on toggle
        const observer = new MutationObserver(() => {
          summary.setAttribute('aria-expanded', details.open);
        });
        
        observer.observe(details, { attributes: true, attributeFilter: ['open'] });
      }
    });
  }
  
  /**
   * Setup Entrance Animations
   */
  setupAnimations() {
    // Intersection Observer für Entrance Animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
        }
      });
    }, observerOptions);
    
    // Observe FAQ items
    document.querySelectorAll('.faq-item-wrapper').forEach(item => {
      observer.observe(item);
    });
  }
  
  /**
   * Public API Methods
   */
  openAllFAQs(sectionId = null) {
    const sections = sectionId ? 
      [document.getElementById(sectionId)] : 
      Array.from(this.sections);
    
    sections.forEach(section => {
      if (section) {
        section.querySelectorAll('.faq-details').forEach(details => {
          if (!details.open) {
            this.openAccordion(details, details.querySelector('.faq-answer-wrapper'));
          }
        });
      }
    });
  }
  
  closeAllFAQs(sectionId = null) {
    const sections = sectionId ? 
      [document.getElementById(sectionId)] : 
      Array.from(this.sections);
    
    sections.forEach(section => {
      if (section) {
        section.querySelectorAll('.faq-details').forEach(details => {
          if (details.open) {
            this.closeAccordion(details, details.querySelector('.faq-answer-wrapper'));
          }
        });
      }
    });
  }
  
  searchFAQ(query, sectionId = null) {
    const sections = sectionId ? 
      [document.getElementById(sectionId)] : 
      Array.from(this.sections);
    
    sections.forEach(section => {
      if (section) {
        this.performSearch(query, section);
      }
    });
  }
}

// Initialize FAQ Component when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.faqComponent = new FAQComponent();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FAQComponent;
}
